import type { ParticleCore, PoolRange } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';

/**
 * PoolRenderer — the render seam of the particles-optimised type: one
 * renderer per pool, uploading that pool's lanes to the GPU each time
 * upload() is called. The interface exists so the backend is swappable
 * (the design's fallback was an instanced-mesh renderer for pre-8.6 Pixi);
 * with the CDN default now 8.6.6 the ParticleContainer backend below is the
 * production path.
 */
export interface PoolRenderer {
	/** The container the host mounts/positions (one per pool). */
	readonly container: PIXI.Container;
	/** Push the pool's current lane values to the render buffers. */
	upload(scaleMultiplier?: number): void;
	destroy(): void;
}

/**
 * ParticleContainerRenderer — PIXI.ParticleContainer (the modern v8.6+
 * class: flat Particle structs, one packed buffer upload, no Sprite /
 * transform / event overhead per particle) over a ParticleCore pool.
 *
 * All particles in a ParticleContainer batch against ONE texture source —
 * satisfied here because every frame in the table comes from the same
 * home-particles atlas.
 *
 * GOTCHA: ParticleContainer does NOT derive bounds from its particles (no
 * per-particle bounds pass — that is the point). Anything bounds-driven
 * (renderer.extract without an explicit frame, getBounds-based tooling)
 * sees an empty rect. Extract probes must pass {target, frame}.
 *
 * The v8 class is reached via `(PIXI as any)` — the ambient PIXI types are
 * still the v5 package (see src/types/globals.d.ts), whose ParticleContainer
 * declaration has the old signature.
 */
export class ParticleContainerRenderer implements PoolRenderer {
	public readonly container: PIXI.Container;

	private _core: ParticleCore;
	private _range: PoolRange;
	private _frames: ReadonlyArray<PIXI.Texture>;
	private _particles: Array<any>; //PIXI.Particle structs, index-aligned with the pool range

	constructor(
		core: ParticleCore,
		range: PoolRange,
		frames: ReadonlyArray<PIXI.Texture>,
		label: string,
		blendMode: string = null
	) {
		this._core = core;
		this._range = range;
		this._frames = frames;

		//position dynamic (per-frame lane -> buffer upload is this type's whole
		//job from stage 2 on); scale/color static for the stage-1 scaffold —
		//update() re-uploads statics when layout re-derives on resize:
		this.container = new (PIXI as any).ParticleContainer({
			dynamicProperties: { position: true, scale: false, rotation: false, color: false }
		});
		//blendMode is container-level here where the reference sets it per
		//sprite — equivalent for the pools that use it (icon + intro set
		//'add' on EVERY particle in the pool: IconParticleProcessor.initIcon,
		//IntroParticleProcessor.startState):
		if (blendMode) {
			(this.container as any).blendMode = blendMode;
		}
		this.container.name = label;
		(this.container as any).interactive = false;
		(this.container as any).interactiveChildren = false;

		//One Particle struct per slot, created up front (structs are ~cheap;
		//the pool's capacity is fixed by HomeFieldConfig):
		this._particles = new Array(range.count);
		for (let i = 0; i < range.count; i++) {
			const particle = new (PIXI as any).Particle(this._frames[0]);
			particle.anchorX = 0.5;
			particle.anchorY = 0.5;
			this._particles[i] = particle;
			(this.container as any).addParticle(particle);
		}
	}

	/** Full-range lane push (position/scale/alpha/tint/frame). Inactive slots
	 *  upload as alpha 0 — no add/remove churn, the flag decides visibility.
	 *  scaleMultiplier mirrors ParticleProperties._setScale's
	 *  `scale * globals.scaleFactorM` sprite-write (the reference applies the
	 *  global display factor at write time, not in the simulated value). */
	public upload(scaleMultiplier: number = 1): void {
		const core = this._core;
		const start = this._range.start;

		for (let i = 0; i < this._particles.length; i++) {
			const lane = start + i;
			const particle = this._particles[i];
			const scale = core.scale[lane] * scaleMultiplier;
			const rgb = core.tint[lane];

			particle.x = core.x[lane];
			particle.y = core.y[lane];
			particle.scaleX = scale;
			particle.scaleY = scale;
			//Rotation is unsmoothed by design — only the path kernel's
			//syncRotation branch writes the lane (see ParticleCore.rotation):
			particle.rotation = core.rotation[lane];
			particle.alpha = core.stateFlags[lane] & 1 ? core.alpha[lane] : 0;
			//Particle.tint consumes raw numbers in BGR byte order (verified
			//empirically on 8.6.6: tint 0xff0000 rendered blue) — lanes store
			//honest 0xRRGGBB, the swap lives only here at the GPU seam:
			particle.tint = ((rgb & 0xff) << 16) | (rgb & 0x00ff00) | ((rgb >> 16) & 0xff);
			particle.texture = this._frames[core.textureId[lane]];
		}

		//Re-upload the static properties too (scale/color/texture UVs changed):
		(this.container as any).update();
	}

	public destroy(): void {
		this.container.parent?.removeChild(this.container);
		(this.container as any).destroy({ children: true });
		this._particles = null;
	}
}

/**
 * SplitBlendParticleRenderer (stage 5) — the screens pool's renderer. The
 * reference flips sprite.blendMode PER PARTICLE between phases (the radial
 * halftone sets 'add' on its particles, the drop machine / swirl set null),
 * which a single ParticleContainer cannot express (blend state is
 * container-level). The design's additive sub-batch: TWO ParticleContainers
 * over the same pool range — a default-blend batch and an 'add' batch — and
 * the blendAdd lane routes each particle's upload (the batch it is not in
 * uploads alpha 0; no add/remove churn).
 *
 * DEVIATION (documented): within the reference container, additive and
 * normal sprites interleave in pool-index draw order; here the additive
 * batch draws entirely ABOVE the normal batch. The live phases are
 * blend-homogeneous (halftone all-'add', drop/swirl all-null), so mixing
 * only occurs for a few cross-fade frames at phase switches — no steady
 * state differs.
 */
export class SplitBlendParticleRenderer implements PoolRenderer {
	public readonly container: PIXI.Container;

	private _core: ParticleCore;
	private _range: PoolRange;
	private _frames: ReadonlyArray<PIXI.Texture>;
	private _normalParticles: Array<any>;
	private _addParticles: Array<any>;
	private _normal: any; //ParticleContainer
	private _add: any; //ParticleContainer, blendMode 'add'

	constructor(core: ParticleCore, range: PoolRange, frames: ReadonlyArray<PIXI.Texture>, label: string) {
		this._core = core;
		this._range = range;
		this._frames = frames;

		this.container = new PIXI.Container();
		this.container.name = label;
		(this.container as any).interactive = false;
		(this.container as any).interactiveChildren = false;

		const makeBatch = (blendMode: string, suffix: string) => {
			const batch = new (PIXI as any).ParticleContainer({
				dynamicProperties: { position: true, scale: false, rotation: false, color: false }
			});
			if (blendMode) {
				batch.blendMode = blendMode;
			}
			batch.name = `${label}-${suffix}`;
			batch.interactive = false;
			batch.interactiveChildren = false;

			const particles = new Array(range.count);
			for (let i = 0; i < range.count; i++) {
				const particle = new (PIXI as any).Particle(this._frames[0]);
				particle.anchorX = 0.5;
				particle.anchorY = 0.5;
				particle.alpha = 0;
				particles[i] = particle;
				batch.addParticle(particle);
			}

			this.container.addChild(batch);
			return { batch: batch, particles: particles };
		};

		const normal = makeBatch(null, 'normal');
		const add = makeBatch('add', 'add');
		this._normal = normal.batch;
		this._normalParticles = normal.particles;
		this._add = add.batch;
		this._addParticles = add.particles;
	}

	/** Same lane push as ParticleContainerRenderer.upload, routed by the
	 *  blendAdd lane: the particle's OTHER batch uploads alpha 0. */
	public upload(scaleMultiplier: number = 1): void {
		const core = this._core;
		const start = this._range.start;

		for (let i = 0; i < this._normalParticles.length; i++) {
			const lane = start + i;
			const isAdd = core.blendAdd[lane] === 1;
			const particle = isAdd ? this._addParticles[i] : this._normalParticles[i];
			const other = isAdd ? this._normalParticles[i] : this._addParticles[i];
			const scale = core.scale[lane] * scaleMultiplier;
			const rgb = core.tint[lane];

			particle.x = core.x[lane];
			particle.y = core.y[lane];
			particle.scaleX = scale;
			particle.scaleY = scale;
			particle.rotation = core.rotation[lane];
			particle.alpha = core.stateFlags[lane] & 1 ? core.alpha[lane] : 0;
			//Particle.tint is BGR — the swap lives only at this GPU seam (see
			//ParticleContainerRenderer.upload):
			particle.tint = ((rgb & 0xff) << 16) | (rgb & 0x00ff00) | ((rgb >> 16) & 0xff);
			particle.texture = this._frames[core.textureId[lane]];

			other.alpha = 0;
		}

		this._normal.update();
		this._add.update();
	}

	public destroy(): void {
		this.container.parent?.removeChild(this.container);
		(this.container as any).destroy({ children: true });
		this._normalParticles = null;
		this._addParticles = null;
		this._normal = null;
		this._add = null;
	}
}
